import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  unacceptedPoliciesQueryOptions,
  policyContentQueryOptions,
  POLICY_QUERY_KEYS,
} from '@/api/queries/policies';
import * as policiesApi from '@/api/endpoints/policies';
import { useAuth } from '@/hooks/use-auth';
import type { PolicyAcceptanceStatus, UsePolicyAcceptanceReturn } from '../types';

export function usePolicyAcceptance(): UsePolicyAcceptanceReturn {
  const queryClient = useQueryClient();
  const { logout: authLogout } = useAuth();

  const { data: unacceptedData, isLoading, isError } = useQuery(
    unacceptedPoliciesQueryOptions()
  );

  const unacceptedPolicies = unacceptedData?.policies ?? [];
  const isFirstAcceptance = unacceptedData?.isFirstAcceptance ?? true;

  const [currentPolicyIndex, setCurrentPolicyIndex] = useState(0);
  const [hasCheckedBox, setHasCheckedBox] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const currentPolicy = unacceptedPolicies[currentPolicyIndex] ?? null;

  const { data: contentData, isLoading: isContentLoading } = useQuery(
    policyContentQueryOptions(
      currentPolicy?.policyId ?? '',
      currentPolicy?.versionId ?? ''
    )
  );

  const status: PolicyAcceptanceStatus = useMemo(() => {
    if (isLoading) return 'loading';
    if (isError) return 'error';
    if (unacceptedPolicies.length === 0) return 'clear';
    return 'blocked';
  }, [isLoading, isError, unacceptedPolicies.length]);

  const acceptCurrent = useCallback(async () => {
    if (!currentPolicy) return;

    setIsAccepting(true);
    try {
      const method = isFirstAcceptance ? 'REGISTRATION' : 'LOGIN_GATE';
      await policiesApi.acceptPolicy(
        currentPolicy.policyId,
        currentPolicy.versionId,
        method
      );

      if (currentPolicyIndex < unacceptedPolicies.length - 1) {
        setCurrentPolicyIndex((prev) => prev + 1);
        setHasCheckedBox(false);
      } else {
        queryClient.invalidateQueries({
          queryKey: POLICY_QUERY_KEYS.unaccepted(),
        });
      }
    } finally {
      setIsAccepting(false);
    }
  }, [currentPolicy, currentPolicyIndex, unacceptedPolicies.length, isFirstAcceptance, queryClient]);

  const logout = useCallback(async () => {
    await authLogout();
  }, [authLogout]);

  return {
    status,
    unacceptedPolicies,
    currentPolicyIndex,
    currentPolicy,
    policyContent: contentData?.content ?? null,
    isContentLoading,
    isAccepting,
    hasCheckedBox,
    setHasCheckedBox,
    acceptCurrent,
    logout,
  };
}
